import { useCallback, useEffect, useRef, useState } from 'react'
import socket from 'shared/utils/socket'
import { EnhanceSocketEvent, EnhanceSocketIncomingEvent } from 'models/app'

type SocketJobHookOptions<TCompletedPayload> = {
  enabled?: boolean;
  createJob: () => Promise<{ jobId: string }>;
  onStarted?: (payload: { jobId: string; message: string }) => void;
  onProgress?: (payload: {
    jobId: string;
    message: string;
    percent: number;
  }) => void;
  onCompleted?: (payload: { jobId: string; result: TCompletedPayload }) => void;
  onError?: (payload: any) => void;
};

type ProgressData = {
  jobId: string
  percent: number
  message: string
}

export const useSocketRequest = <TPayload>({
  enabled = true,
  createJob,
  onStarted,
  onProgress,
  onCompleted,
  onError
}: SocketJobHookOptions<TPayload>) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<TPayload | null>(null)
  const [progressData, setProgressData] = useState<ProgressData | null>(null)
  const [retryIndex, setRetryIndex] = useState(0)
  const [forceEnabled, setForceEnabled] = useState(false)

  const jobStarted = useRef(false)
  const currentJobId = useRef<string | null>(null)

  const retry = useCallback(() => {
    setRetryIndex((i) => i + 1)
    jobStarted.current = false
    currentJobId.current = null
    setIsLoaded(false)
    setIsError(false)
    setForceEnabled(true)
  }, [])

  useEffect(() => {
    if ((!enabled && !forceEnabled) || jobStarted.current) {
      if (!enabled && !forceEnabled) {
        setIsLoaded(true)
      }
      return
    }

    jobStarted.current = true

    const handleStarted = (payload: any) => {
      if (payload.jobId !== currentJobId.current) return
      onStarted?.(payload)
    }

    const handleProgress = (payload: ProgressData) => {
      if (payload.jobId !== currentJobId.current) return
      onProgress?.(payload)
      setProgressData(payload)
    }

    const handleCompleted = (payload: any) => {
      if (payload.jobId !== currentJobId.current) return
      onCompleted?.(payload)
      setData(payload.result)
      setIsLoaded(true)
      setForceEnabled(false)
      cleanup()
    }

    const handleError = (payload: any) => {
      if (payload.jobId !== currentJobId.current) return
      onError?.(payload)
      setIsLoaded(true)
      setIsError(true)
      setForceEnabled(false)
      cleanup()
    }

    const handleSubscribed = (payload: any) => { }

    const cleanup = () => {
      socket.off(EnhanceSocketEvent.PROCESSING_STARTED, handleStarted)
      socket.off(EnhanceSocketEvent.PROCESSING_PROGRESS, handleProgress)
      socket.off(EnhanceSocketEvent.PROCESSING_COMPLETED, handleCompleted)
      socket.off(EnhanceSocketEvent.PROCESSING_ERROR, handleError)
      socket.off(EnhanceSocketEvent.SUBSCRIBED, handleSubscribed)
      currentJobId.current = null
    }

    const executeJob = async () => {
      try {
        setIsLoaded(false)
        setIsError(false)

        // 1. Убеждаемся что WebSocket подключен
        if (!socket.connected) {
          socket.connect()

          // Ждем подключения
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => {
              reject(new Error('WebSocket connection timeout'))
            }, 5000)

            if (socket.connected) {
              clearTimeout(timeout)
              resolve()
              return
            }

            const onConnect = () => {
              clearTimeout(timeout)
              socket.off('connect', onConnect)
              socket.off('connect_error', onError)
              resolve()
            }

            const onError = (error: any) => {
              clearTimeout(timeout)
              socket.off('connect', onConnect)
              socket.off('connect_error', onError)
              reject(error)
            }

            socket.on('connect', onConnect)
            socket.on('connect_error', onError)
          })
        }

        // 2. Регистрируем обработчики событий ДО создания job
        socket.on(EnhanceSocketEvent.PROCESSING_STARTED, handleStarted)
        socket.on(EnhanceSocketEvent.PROCESSING_PROGRESS, handleProgress)
        socket.on(EnhanceSocketEvent.PROCESSING_COMPLETED, handleCompleted)
        socket.on(EnhanceSocketEvent.PROCESSING_ERROR, handleError)
        socket.on(EnhanceSocketEvent.SUBSCRIBED, handleSubscribed)

        // 3. Создаем job и получаем jobId
        const res = await createJob()
        currentJobId.current = res.jobId

        // 4. Подписываемся на события для конкретного jobId
        socket.emit(EnhanceSocketIncomingEvent.SUBSCRIBE_TO_JOB, {
          jobId: currentJobId.current
        })

        // 5. Ждем подтверждения подписки
        await new Promise<void>((resolve) => {
          const timeout = setTimeout(() => {
            resolve()
          }, 2000)

          const onSubscribed = (payload: any) => {
            if (payload.jobId === currentJobId.current) {
              clearTimeout(timeout)
              socket.off(EnhanceSocketEvent.SUBSCRIBED, onSubscribed)
              resolve()
            }
          }

          socket.on(EnhanceSocketEvent.SUBSCRIBED, onSubscribed)
        })
      } catch {
        setIsLoaded(true)
        setIsError(true)
        setForceEnabled(false)
        cleanup()
      }
    }

    executeJob()

    return cleanup
  }, [
    enabled,
    retryIndex,
    forceEnabled,
    createJob,
    onStarted,
    onProgress,
    onCompleted,
    onError
  ])

  return { isLoaded, isError, retry, data, progressData }
}
